import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../App';

/**
 * Live genres context.
 *
 * Single source of truth for the music genre list across Register, Artist
 * dialogs, Admin dialogs, and any future form. Backed by `GET /api/genres`
 * (active genres only, server-sorted). The provider fetches once on mount,
 * caches in state, and exposes `refresh()` so the Admin Genres tab can poke
 * every consumer to re-pull immediately after a create/edit/delete.
 *
 * Forms should default the genre value to `null` (or empty string) — genre is
 * NEVER required. Use `useGenres()` to read; never re-import the old static
 * GENRES array.
 */
const GenresContext = createContext({ genres: [], loading: true, refresh: async () => {}, error: null });

export function GenresProvider({ children }) {
  const [genres, setGenres] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/genres`);
      setGenres(r.data?.genres || []);
      setError(null);
    } catch (e) {
      console.error('[genres] failed to load', e?.message || e);
      setError(e?.message || 'Failed to load genres');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <GenresContext.Provider value={{ genres, loading, refresh, error }}>
      {children}
    </GenresContext.Provider>
  );
}

export function useGenres() {
  return useContext(GenresContext);
}

/**
 * Internal sentinel: shadcn's <Select> can't have a SelectItem with value="".
 * We use this token to represent "no genre" and translate to `null` on submit.
 */
export const NO_GENRE_VALUE = '__none__';

/** Normalize a form value to the API value: empty / sentinel → null. */
export function genreToApi(value) {
  if (!value || value === NO_GENRE_VALUE) return null;
  return value;
}

/** Normalize an API value (possibly null) to the form Select value. */
export function genreToForm(apiValue) {
  return apiValue || NO_GENRE_VALUE;
}
